import type {
  DashboardKind,
  DashboardSettingsResponse,
  DashboardWidgetKey,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDashboardSettings,
  updateDashboardCashThreshold,
  updateDashboardWidgets,
} from "@/lib/api/dashboard-settings";
import { dashboardKeys } from "./use-dashboard";

export const dashboardSettingsKeys = {
  all: ["dashboard-settings"] as const,
  save: ["dashboard-settings", "save"] as const,
};

export function useDashboardSettings() {
  const { data, error, isLoading } = useQuery({
    queryKey: dashboardSettingsKeys.all,
    queryFn: getDashboardSettings,
  });
  return { settings: (data as DashboardSettingsResponse | undefined) ?? null, error, isLoading };
}

/** Every settings save, chained so each waits for the one before it. */
let saveQueue: Promise<unknown> = Promise.resolve();

/** Run `save` once every earlier settings save has settled. */
function queued<T>(save: () => Promise<T>): Promise<T> {
  const run = saveQueue.catch(() => undefined).then(save);
  saveQueue = run;
  return run;
}

/**
 * Every switch, drag and threshold edit saves on its own. The change shows at
 * once; the dashboards refetch after.
 *
 * Saves reach the server one after another: each sends the whole widget list,
 * and an older list arriving last would otherwise overwrite a newer one. (Not
 * TanStack's mutation `scope` — a queued scoped mutation waits for the tab to
 * be visible, so a switch flipped just before leaving the tab would not be
 * sent until the designer came back.) The server's answer replaces the shown
 * setup only once no later save is still queued, so a queued change never
 * flickers back. A refused save reloads the setup from the server rather than
 * restoring a snapshot — with saves queued, the snapshot can hold a change the
 * server also refused.
 */
function useSettingsMutation<V>(
  save: (variables: V) => Promise<DashboardSettingsResponse>,
  apply: (current: DashboardSettingsResponse, variables: V) => DashboardSettingsResponse,
) {
  const queryClient = useQueryClient();
  // A save still counts as pending in its own callbacks, so 1 means nothing is queued behind it.
  const isLastSave = () =>
    queryClient.isMutating({ mutationKey: dashboardSettingsKeys.save }) === 1;
  return useMutation({
    mutationKey: dashboardSettingsKeys.save,
    mutationFn: (variables: V) => queued(() => save(variables)),
    onMutate: async (variables: V) => {
      await queryClient.cancelQueries({ queryKey: dashboardSettingsKeys.all });
      const previous = queryClient.getQueryData<DashboardSettingsResponse>(
        dashboardSettingsKeys.all,
      );
      if (previous) {
        queryClient.setQueryData(dashboardSettingsKeys.all, apply(previous, variables));
      }
    },
    onError: () => {
      if (isLastSave()) queryClient.invalidateQueries({ queryKey: dashboardSettingsKeys.all });
    },
    onSuccess: (saved) => {
      if (isLastSave()) queryClient.setQueryData(dashboardSettingsKeys.all, saved);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
  });
}

export function useUpdateDashboardWidgets() {
  return useSettingsMutation(
    ({ dashboard, widgets }: { dashboard: DashboardKind; widgets: DashboardWidgetKey[] }) =>
      updateDashboardWidgets(dashboard, widgets),
    (current, { dashboard, widgets }) => ({ ...current, [dashboard]: widgets }),
  );
}

export function useUpdateDashboardCashThreshold() {
  return useSettingsMutation(
    (cashThreshold: number) => updateDashboardCashThreshold(cashThreshold),
    (current, cashThreshold) => ({ ...current, cashThreshold }),
  );
}
